import type { PublicProfilePayload } from './types';

export const PUBLIC_PROFILE_SECTIONS = [
  'overview',
  'timeline',
  'stories',
  'media',
  'achievements',
  'journey',
  'about',
] as const;

export type PublicProfileSection = (typeof PUBLIC_PROFILE_SECTIONS)[number];
export type PublicProfileScope = PublicProfileSection | 'all';

export type PublicProfileQueryPlan = {
  scope: PublicProfileScope;
  includeStories: boolean;
  storyLimit: number;
  includePulses: boolean;
  pulseLimit: number;
  includeRecords: boolean;
  recordLimit: number;
  includeAchievementMedia: boolean;
  includeAchievementContext: boolean;
  includeVerificationCount: boolean;
  includeProofCards: boolean;
  proofCardLimit: number;
  includeJourney: boolean;
  journeyLimit: number;
  includeFamily: boolean;
  familyRowLimit: number;
  familyItemLimit: number;
  includeCircles: boolean;
  circleLimit: number;
  circleMemoryLimit: number;
  includeSoundtrack: boolean;
  includeViewerState: boolean;
  includeLightweightCounts: boolean;
};

/** Missing scopes retain the legacy full-payload behavior for existing callers. */
export function resolvePublicProfileScope(value: string | null): PublicProfileScope {
  if (value === 'all') return 'all';
  return PUBLIC_PROFILE_SECTIONS.includes(value as PublicProfileSection)
    ? (value as PublicProfileSection)
    : 'all';
}

export function getPublicProfileQueryPlan(scope: PublicProfileScope): PublicProfileQueryPlan {
  const all = scope === 'all';
  const overview = scope === 'overview';
  const timeline = scope === 'timeline';
  const stories = scope === 'stories';
  const media = scope === 'media';
  const achievements = scope === 'achievements';
  const journey = scope === 'journey';
  const about = scope === 'about';

  return {
    scope,
    includeStories: all || overview || timeline || stories || media,
    storyLimit: overview ? 4 : 40,
    includePulses: all || overview,
    pulseLimit: overview ? 3 : 5,
    includeRecords: all || overview || timeline || media || achievements,
    recordLimit: overview ? 3 : 40,
    includeAchievementMedia: all || overview || timeline || media || achievements,
    includeAchievementContext: all || overview || timeline || achievements,
    includeVerificationCount: all || overview || achievements,
    includeProofCards: all || overview || achievements,
    proofCardLimit: overview ? 3 : 30,
    includeJourney: all || overview || timeline || journey,
    journeyLimit: overview ? 4 : all ? 5 : 40,
    includeFamily: all || overview || timeline || about,
    familyRowLimit: overview ? 3 : 24,
    familyItemLimit: overview ? 3 : 60,
    includeCircles: all || overview || about,
    circleLimit: overview ? 3 : 12,
    circleMemoryLimit: overview ? 12 : 100,
    includeSoundtrack: all || overview,
    includeViewerState: all || overview,
    includeLightweightCounts: all || overview,
  };
}

export function sectionPayloadFields(
  section: PublicProfileSection
): ReadonlyArray<
  | 'stories'
  | 'pulseHighlights'
  | 'records'
  | 'proofCards'
  | 'journeyMoments'
  | 'familyItems'
  | 'familySummary'
  | 'circles'
  | 'featuredStory'
  | 'featuredRecord'
> {
  switch (section) {
    case 'overview':
      return [
        'stories',
        'pulseHighlights',
        'records',
        'proofCards',
        'journeyMoments',
        'familyItems',
        'familySummary',
        'circles',
        'featuredStory',
        'featuredRecord',
      ];
    case 'timeline':
      return ['stories', 'records', 'journeyMoments', 'familyItems', 'familySummary'];
    case 'stories':
      return ['stories'];
    case 'media':
      return ['stories', 'records'];
    case 'achievements':
      return ['records', 'proofCards'];
    case 'journey':
      return ['journeyMoments'];
    case 'about':
      return ['familyItems', 'familySummary', 'circles'];
  }
}

function fieldHasDeeperPayload(
  field: ReturnType<typeof sectionPayloadFields>[number],
  loadedSections: ReadonlySet<PublicProfileSection>
): boolean {
  return [...loadedSections].some(
    (loadedSection) =>
      loadedSection !== 'overview' && sectionPayloadFields(loadedSection).includes(field)
  );
}

function fieldRemainsVisible(
  field: ReturnType<typeof sectionPayloadFields>[number],
  incoming: PublicProfilePayload
): boolean {
  switch (field) {
    case 'stories':
    case 'pulseHighlights':
    case 'featuredStory':
      return incoming.visibility.show_stories;
    case 'records':
    case 'featuredRecord':
      return incoming.visibility.show_records;
    case 'proofCards':
      return incoming.visibility.show_proof_cards;
    case 'journeyMoments':
      return (
        incoming.isOwner ||
        incoming.visibility.show_journey_preview ||
        incoming.visibility.journey_map_visibility === 'preview' ||
        incoming.visibility.journey_map_visibility === 'full'
      );
    case 'familyItems':
    case 'familySummary':
      return incoming.visibility.show_family_preview;
    case 'circles':
      return incoming.visibility.show_public_circles;
  }
}

/** Merge a section response without erasing collections loaded by another tab. */
export function mergePublicProfileSectionPayload(
  current: PublicProfilePayload,
  incoming: PublicProfilePayload,
  section: PublicProfileSection,
  loadedSections: ReadonlySet<PublicProfileSection> = new Set()
): PublicProfilePayload {
  const next: PublicProfilePayload = {
    ...current,
    profile: incoming.profile,
    handle: incoming.handle,
    visibility: incoming.visibility,
    isOwner: incoming.isOwner,
    viewerId: incoming.viewerId,
  };

  if (section === 'overview') {
    next.stats = incoming.stats;
    next.isFollowing = incoming.isFollowing;
    next.soundtrack = incoming.soundtrack;
    next.soundtrackState = incoming.soundtrackState;
    next.verificationLevel = incoming.verificationLevel;
  } else if (section === 'achievements') {
    next.verificationLevel = incoming.verificationLevel;
  }

  for (const field of sectionPayloadFields(section)) {
    const preserveDeeperCollection =
      section === 'overview' &&
      field !== 'featuredStory' &&
      field !== 'featuredRecord' &&
      fieldRemainsVisible(field, incoming) &&
      fieldHasDeeperPayload(field, loadedSections);
    if (preserveDeeperCollection) continue;

    switch (field) {
      case 'stories':
        next.stories = incoming.stories;
        break;
      case 'pulseHighlights':
        next.pulseHighlights = incoming.pulseHighlights;
        break;
      case 'records':
        next.records = incoming.records;
        break;
      case 'proofCards':
        next.proofCards = incoming.proofCards;
        break;
      case 'journeyMoments':
        next.journeyMoments = incoming.journeyMoments;
        break;
      case 'familyItems':
        next.familyItems = incoming.familyItems;
        break;
      case 'familySummary':
        next.familySummary = incoming.familySummary;
        break;
      case 'circles':
        next.circles = incoming.circles;
        break;
      case 'featuredStory':
        next.featuredStory = incoming.featuredStory;
        break;
      case 'featuredRecord':
        next.featuredRecord = incoming.featuredRecord;
        break;
    }
  }

  return next;
}
